// Normalised representation of a patient demographic record used by the
// PDS Sync comparison view. Both the local TITANVerse record and the NHS
// Personal Demographics Service (PDS) record are reduced to this shape so
// they can be compared field-by-field.
//
// Phones and addresses are lists because PDS can hold several active entries
// (e.g. home + mobile + textphone, or a home + temporary address).

export interface PdsContactPoint {
  label: string;
  value: string;
  preferred?: boolean;
}

export interface PdsAddressEntry {
  label: string;
  text: string;
  preferred?: boolean;
}

export interface PdsDemographics {
  name: string;
  dateOfBirth: string;
  gender: string;
  nhsNumber: string;
  phones: PdsContactPoint[];
  email: string;
  addresses: PdsAddressEntry[];
  gpPractice: string;
  nominatedPharmacy: string;
  preferredContactMethod: string;
  language: string;
  birthPlace: string;
  deceased: string;
}

export interface PdsRecord extends PdsDemographics {
  nhsNumberVerified: boolean;
  versionId: string;
}

export type PdsFieldKind = "text" | "phones" | "addresses";

export const PDS_FIELDS: {
  key: keyof PdsDemographics;
  label: string;
  kind: PdsFieldKind;
}[] = [
  { key: "name", label: "Name", kind: "text" },
  { key: "dateOfBirth", label: "Date of birth", kind: "text" },
  { key: "gender", label: "Gender", kind: "text" },
  { key: "nhsNumber", label: "NHS number", kind: "text" },
  { key: "phones", label: "Contact numbers", kind: "phones" },
  { key: "email", label: "Email", kind: "text" },
  { key: "addresses", label: "Addresses", kind: "addresses" },
  { key: "gpPractice", label: "GP practice (ODS)", kind: "text" },
  { key: "nominatedPharmacy", label: "Nominated pharmacy (ODS)", kind: "text" },
  { key: "preferredContactMethod", label: "Preferred contact method", kind: "text" },
  { key: "language", label: "Language", kind: "text" },
  { key: "birthPlace", label: "Place of birth", kind: "text" },
  { key: "deceased", label: "Deceased", kind: "text" },
];

// Simple string shape the rest of the app passes in for the local record.
export interface LocalRecordInput {
  name?: string;
  dateOfBirth?: string;
  gender?: string;
  nhsNumber?: string;
  phone?: string;
  email?: string;
  address?: string;
  gpPractice?: string;
  nominatedPharmacy?: string;
  preferredContactMethod?: string;
  language?: string;
  birthPlace?: string;
  deceased?: string;
}

export function toPdsDemographics(input: any): PdsRecord {
  if (!input) {
    return {
      name: "",
      dateOfBirth: "",
      gender: "",
      nhsNumber: "",
      nhsNumberVerified: false,
      versionId: "",
      phones: [],
      email: "",
      addresses: [],
      gpPractice: "",
      nominatedPharmacy: "",
      preferredContactMethod: "",
      language: "",
      birthPlace: "",
      deceased: "",
    };
  }

  // If already normalized PdsRecord/PdsDemographics shape
  if (
    typeof input.name === "string" &&
    Array.isArray(input.phones) &&
    Array.isArray(input.addresses) &&
    input.name !== ""
  ) {
    return input as PdsRecord;
  }

  // 1. Name
  let name = input.patientName || input.name || "";
  if (!name) {
    name = [input.title, input.foreName, input.middleName, input.surName]
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  // 2. Date of Birth
  let dateOfBirth = input.dob || input.dateOfBirth || input.birthDate || "";
  if (dateOfBirth && String(dateOfBirth).startsWith("0001-01-01")) {
    dateOfBirth = "";
  } else if (dateOfBirth && String(dateOfBirth).includes("T")) {
    dateOfBirth = String(dateOfBirth).split("T")[0];
  }

  // 3. Gender / Sex
  let gender = "";
  if (typeof input.sex === "number") {
    gender = input.sex === 1 ? "Male" : input.sex === 2 ? "Female" : "";
  }
  if (!gender) {
    if (typeof input.sex === "string") gender = input.sex;
    else if (typeof input.gender === "string") gender = input.gender;
  }

  // 4. NHS Number — strip any "NHS|" style prefix and separators, keep digits only
  const nhsNumber = input.nhsNumber
    ? String(input.nhsNumber).replace(/\D/g, "")
    : "";
  const nhsNumberVerified = !!input.isNHSNumberValid;

  // 5. Phone / Contacts — some PDS records mistakenly put an email address
  const isEmailLike = (v: string) => /\S+@\S+\.\S+/.test(v);
  const phones: PdsContactPoint[] = [];
  let emailFromContacts = "";
  if (Array.isArray(input.contacts) && input.contacts.length > 0) {
    input.contacts.forEach((c: any, index: number) => {
      if (c?.value && String(c.value).trim() !== "") {
        const val = String(c.value).trim();
        if (isEmailLike(val)) {
          if (!emailFromContacts) emailFromContacts = val;
          return;
        }
        phones.push({
          label: c.contactType === 4 ? "Mobile" : c.contactType === 1 ? "Home" : "Phone",
          value: val.replace(/\s+/g, ""),
          preferred: index === 0,
        });
      }
    });
  }
  if (phones.length === 0) {
    const phoneVal = input.telephone || input.phone || input.mobile;
    if (phoneVal && !isEmailLike(String(phoneVal).trim())) {
      phones.push({
        label: "Mobile",
        value: String(phoneVal).replace(/\s+/g, "").trim(),
        preferred: true,
      });
    }
  }

  // 6. Email — prefer the explicit email field; fall back to an email-like
  // value found in contacts (see step 5) only if the field itself is empty.
  const email = input.email
    ? String(input.email).trim()
    : emailFromContacts;

  // 7. Addresses
  const addresses: PdsAddressEntry[] = [];
  let addrText = input.address || input.fullAddress || "";

  if (!addrText && input.preferredAddress?.address) {
    const a = input.preferredAddress.address;
    addrText = [
      a.addressLine1 || a.houseName,
      a.addressLine2 || a.streetName,
      a.locality,
      a.town,
      a.county,
      a.postcode,
    ]
      .filter(Boolean)
      .join(", ");
  }

  if (addrText) {
    addresses.push({
      label: "Home",
      text: addrText,
      preferred: true,
    });
  }

  // 8. GP Practice
  let gpPractice = "";
  if (input.doctor) {
    gpPractice = [input.doctor.gpName, input.doctor.surgeryName, input.doctor.surgeryAddress]
      .filter(Boolean)
      .join(" - ");
  }
  if (!gpPractice) {
    if (typeof input.gpPractice === "string" && input.gpPractice) {
      gpPractice = input.gpPractice;
    } else if (typeof input.surgery === "string" && input.surgery) {
      gpPractice = input.surgery;
    } else if (input.surgery && typeof input.surgery === "object") {
      gpPractice = input.surgery.name || input.surgery.Name || "";
    }
  }

  // 9. Nominated Pharmacy
  const nominatedPharmacy = input.nominatedPharmacy || input.ods || "";

  // 10. Preferred Contact Method
  const preferredContactMethod = input.preferredContactMethod || "";

  // 11. Language
  const language = input.language || "";

  // 12. Birth Place
  const birthPlace = input.birthPlace || "";

  // 13. Deceased
  const deceased = input.deceased || "";

  return {
    name,
    dateOfBirth,
    gender,
    nhsNumber,
    nhsNumberVerified,
    versionId: input.patientID || "",
    phones,
    email,
    addresses,
    gpPractice,
    nominatedPharmacy,
    preferredContactMethod,
    language,
    birthPlace,
    deceased,
  };
}

export function toLocalDemographics(
  input: LocalRecordInput = {},
): PdsDemographics {
  return toPdsDemographics(input);
}

// ── Minimal FHIR R4 Patient typing (only what we read) ──────────────────────

interface FhirCoding {
  system?: string;
  code?: string;
  display?: string;
}
interface FhirCodeableConcept {
  coding?: FhirCoding[];
}
interface FhirExtension {
  url: string;
  valueString?: string;
  valueBoolean?: boolean;
  valueDateTime?: string;
  valueCodeableConcept?: FhirCodeableConcept;
  valueCoding?: FhirCoding;
  valueAddress?: { city?: string; district?: string; country?: string };
  valueReference?: { identifier?: { system?: string; value?: string } };
  extension?: FhirExtension[];
}
interface FhirName {
  use?: string;
  prefix?: string[];
  given?: string[];
  family?: string;
}
interface FhirTelecom {
  system?: string;
  value?: string;
  use?: string;
  extension?: FhirExtension[];
}
interface FhirAddress {
  use?: string;
  text?: string;
  line?: string[];
  postalCode?: string;
}
interface FhirIdentifier {
  system?: string;
  value?: string;
  extension?: FhirExtension[];
}
interface FhirReference {
  identifier?: { system?: string; value?: string };
}
export interface FhirPatient {
  resourceType: "Patient";
  id: string;
  meta?: { versionId?: string };
  identifier?: FhirIdentifier[];
  name?: FhirName[];
  gender?: string;
  birthDate?: string;
  deceasedDateTime?: string;
  generalPractitioner?: FhirReference[];
  extension?: FhirExtension[];
  telecom?: FhirTelecom[];
  address?: FhirAddress[];
}

const findExt = (exts: FhirExtension[] | undefined, urlPart: string) =>
  exts?.find((e) => e.url.includes(urlPart));

const codingDisplay = (cc?: FhirCodeableConcept) =>
  cc?.coding?.[0]?.display ?? cc?.coding?.[0]?.code ?? "";

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const USE_LABELS: Record<string, string> = {
  home: "Home",
  mobile: "Mobile",
  work: "Work",
  temp: "Temporary",
  old: "Previous",
  billing: "Billing",
};

const useLabel = (use?: string) =>
  use ? (USE_LABELS[use] ?? cap(use)) : "Contact";

function telecomLabel(t: FhirTelecom): string {
  if (t.system === "other") {
    const other = findExt(t.extension, "OtherContactSystem");
    return other?.valueCoding?.display ?? "Other";
  }
  return useLabel(t.use);
}

export function parsePdsPatient(patient: FhirPatient): PdsRecord {
  const officialName =
    patient.name?.find((n) => n.use === "usual") ?? patient.name?.[0];
  const name = officialName
    ? [
        officialName.prefix?.join(" "),
        officialName.given?.join(" "),
        officialName.family,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const nhsIdentifier = patient.identifier?.find((i) =>
    i.system?.includes("nhs-number"),
  );
  const verification = findExt(
    nhsIdentifier?.extension,
    "NHSNumberVerificationStatus",
  );
  const nhsNumberVerified =
    verification?.valueCodeableConcept?.coding?.[0]?.code === "01";

  // All active phone-like contact points (phone, mobile, textphone, ...).
  const phones: PdsContactPoint[] = (patient.telecom ?? [])
    .filter((t) => t.system !== "email" && t.value)
    .map((t) => ({ label: telecomLabel(t), value: t.value as string }));

  const email =
    patient.telecom?.find((t) => t.system === "email")?.value ?? "";

  // All addresses, labelled by use (Home, Temporary, ...).
  const addresses: PdsAddressEntry[] = (patient.address ?? []).map((a) => ({
    label: useLabel(a.use),
    text: [a.text, ...(a.line ?? []), a.postalCode].filter(Boolean).join(", "),
  }));

  const gpPractice = patient.generalPractitioner?.[0]?.identifier?.value ?? "";

  const nominated = findExt(patient.extension, "NominatedPharmacy");
  const nominatedPharmacy = nominated?.valueReference?.identifier?.value ?? "";

  const communication = findExt(patient.extension, "NHSCommunication");
  const language = codingDisplay(
    findExt(communication?.extension, "language")?.valueCodeableConcept,
  );

  const contactPref = findExt(patient.extension, "ContactPreference");
  const preferredContactMethod = codingDisplay(
    findExt(contactPref?.extension, "PreferredContactMethod")
      ?.valueCodeableConcept,
  );

  const birthPlaceExt = findExt(patient.extension, "patient-birthPlace");
  const birthPlace = [
    birthPlaceExt?.valueAddress?.city,
    birthPlaceExt?.valueAddress?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const deceased = patient.deceasedDateTime
    ? patient.deceasedDateTime.slice(0, 10)
    : "";

  return {
    name,
    dateOfBirth: patient.birthDate ?? "",
    gender: patient.gender ? cap(patient.gender) : "",
    nhsNumber: nhsIdentifier?.value ?? "",
    nhsNumberVerified,
    versionId: patient.meta?.versionId ?? "",
    phones,
    email,
    addresses,
    gpPractice,
    nominatedPharmacy,
    preferredContactMethod,
    language,
    birthPlace,
    deceased,
  };
}
